import React, { useState, useRef, useEffect } from 'react';
import { useUserPermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/roles';

const SettingsMenu = ({
    user,
    onSignOut,
    isAdmin,
    onAdminEditCards,
    onAdminManagement,
    onUserManagement,
    onDeveloperPage,
    onLlmSettings,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    // Get user permissions
    const { hasPermission, isSuperUser } = useUserPermissions(user);
    
    // User has admin capabilities if they're an admin OR a super user with certain permissions
    const canManageHomeCards = hasPermission(PERMISSIONS.MANAGE_HOME_CARDS);
    const canAccessBulkUpload = hasPermission(PERMISSIONS.BULK_UPLOAD);
    const canManageTithis = hasPermission(PERMISSIONS.MANAGE_TITHIS);
    const canManageEvents = hasPermission(PERMISSIONS.MANAGE_EVENTS);
    const canManageCalendar = hasPermission(PERMISSIONS.MANAGE_CALENDAR);
    const showAdminSection = isAdmin || isSuperUser || canManageHomeCards || canAccessBulkUpload || canManageTithis || canManageEvents || canManageCalendar;

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Calculate user initials
    const getInitials = () => {
        if (!user) return '?';
        if (user.displayName) {
            const names = user.displayName.split(' ');
            if (names.length >= 2) {
                return (names[0][0] + names[names.length - 1][0]).toUpperCase();
            }
            return names[0].substring(0, 2).toUpperCase();
        }
        return user.email ? user.email.substring(0, 2).toUpperCase() : '?';
    };

    return (
    <div className="settings-root relative" ref={menuRef}>
            {/* Menu Button - Profile Circle */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 rounded-full hover:bg-gray-100 transition-all duration-200 group p-1"
                title="User Profile & Settings"
            >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white shadow-md group-hover:shadow-lg transition-all transform group-hover:scale-105 border-2 border-white">
                    <span className="font-bold text-sm tracking-wide">{getInitials()}</span>
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    {/* User Info */}
                    <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Logged in as</p>
                        <p className="text-sm font-medium text-gray-700 truncate">{user.email}</p>
                    </div>

                    {/* Admin/Super User Options */}
                    {showAdminSection && (
                        <div className="px-4 py-2 border-t border-gray-100">
                            {canManageHomeCards && (
                                <button
                                    onClick={() => {
                                        onAdminEditCards();
                                        setIsOpen(false);
                                    }}
                                    className="w-full text-left px-2 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center space-x-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    <span>Manage Home Cards</span>
                                </button>
                            )}

                            {canAccessBulkUpload && (
                                <button
                                    onClick={() => {
                                        onAdminManagement();
                                        setIsOpen(false);
                                    }}
                                    className="w-full text-left px-2 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-2 mt-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span>Admin Management</span>
                                </button>
                            )}

                            {/* User Management now a top-level settings item (not nested under Admin Management) */}
                            {(isAdmin || hasPermission(PERMISSIONS.MANAGE_USERS)) && (
                                <button
                                    onClick={() => {
                                        if (onUserManagement) onUserManagement();
                                        setIsOpen(false);
                                    }}
                                    className="w-full text-left px-2 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center space-x-2 mt-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-3-3h-2" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20H4v-2a3 3 0 013-3h2" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10a4 4 0 108 0 4 4 0 00-8 0z" />
                                    </svg>
                                    <span>User Management</span>
                                </button>
                            )}
                            
                        </div>
                    )}

                    {/* Settings (per-user config) */}
                    {onLlmSettings && (
                        <div className="px-4 py-2 border-t border-gray-100">
                            <button
                                onClick={() => {
                                    onLlmSettings();
                                    setIsOpen(false);
                                }}
                                className="w-full text-left px-2 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center space-x-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>Settings</span>
                            </button>
                        </div>
                    )}

                    {/* Sign Out Option */}
                    <div className="px-4 py-2">
                        <button
                            onClick={() => {
                                onSignOut();
                                setIsOpen(false);
                            }}
                            className="w-full text-left px-2 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>Sign Out</span>
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SettingsMenu;